/**
 * How dangerous a place is to be in right now. A sector's reported-crime score becomes one of six
 * levels, and the hour of day and the way the traveller is moving can lift that level. Everything
 * here is pure; the Android service reimplements it in Kotlin against `danger-cases.json`, so a
 * change to any number below must change that table too.
 *
 * None of these functions throws. They run inside the Android foreground location service, where an
 * exception is a crash in the one feature that has to keep working while somebody is walking around,
 * so a bad input degrades instead. The rule is the same for every function: a non-finite input
 * (NaN or either infinity) means "no information". A non-finite score fails cautious, at the top
 * level; a non-finite hour is neutral, a multiplier of 1.0; a non-finite speed holds the previous
 * mode; a non-finite alert threshold falls back to the default; a non-finite heading, speed or
 * lookahead projects nowhere, so the position comes back unchanged; and a non-finite coordinate is
 * in no sector. The rule is stated once more in `danger-cases.json`, with a case for each, because
 * Kotlin's `toInt()` turns NaN into 0 and would otherwise quietly disagree.
 */

import crowd from "./crowd-curve.json" with { type: "json" };
import incidents from "./incident-curve.json" with { type: "json" };

export type Mode = "FOOT" | "VEHICLE";
export type Level = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * Upper edges of levels 1 to 5; anything above the last is level 6. The same edges serve both
 * modes. Re-banding each mode against itself would force a dozen sectors into red even in a car,
 * hiding the real finding, which is that a car makes almost everywhere safer.
 */
export const BANDS = [7, 9, 11, 14, 25] as const;

/**
 * A score on a band edge belongs to the lower level, so level 1 is `score <= 7`. A score that is not
 * finite cannot be placed, and is shown as the top level rather than as safe, `-Infinity` included.
 */
export function levelOf(score: number): Level {
  if (!Number.isFinite(score)) return 6;
  for (let i = 0; i < BANDS.length; i++) if (score <= BANDS[i]!) return (i + 1) as Level;
  return 6;
}

/**
 * What each reported crime counts for, by the way you are moving. Keys are the crime names the
 * hoyodecrimen API uses. Home burglary and delivery-driver robbery are in neither set: a visitor
 * is exposed to neither.
 */
export const FOOT_WEIGHTS: Record<string, number> = {
  "HOMICIDIO DOLOSO": 15,
  SECUESTRO: 15,
  "LESIONES POR ARMA DE FUEGO": 10,
  VIOLACION: 10,
  "ROBO A TRANSEUNTE C.V.": 10,
  "ROBO A CUENTAHABIENTE C.V.": 6,
  "ROBO A TRANSEUNTE S.V.": 4,
  "ROBO A BORDO DE METRO C.V.": 2,
  "ROBO A BORDO DE METRO S.V.": 2,
  "ROBO A NEGOCIO C.V.": 2,
};

/** Pedestrian mugging is absent here on purpose: it cannot reach somebody inside a car. */
export const VEHICLE_WEIGHTS: Record<string, number> = {
  "HOMICIDIO DOLOSO": 15,
  SECUESTRO: 15,
  "LESIONES POR ARMA DE FUEGO": 10,
  "ROBO DE VEHICULO AUTOMOTOR C.V.": 10,
  "ROBO A BORDO DE TAXI C.V.": 8,
  "ROBO A BORDO DE MICROBUS C.V.": 6,
  "ROBO A TRANSPORTISTA C.V.": 4,
  "ROBO DE VEHICULO AUTOMOTOR S.V.": 3,
  "ROBO A BORDO DE MICROBUS S.V.": 2,
};

/**
 * The English name of every crime either weight map counts, keyed as the hoyodecrimen API names it.
 * "C.V." is con violencia, "S.V." sin violencia.
 */
export const CRIME_LABELS: Record<string, string> = {
  "HOMICIDIO DOLOSO": "Homicide",
  SECUESTRO: "Kidnapping",
  "LESIONES POR ARMA DE FUEGO": "Firearm injury",
  VIOLACION: "Rape",
  "ROBO A TRANSEUNTE C.V.": "Pedestrian robbery, violent",
  "ROBO A TRANSEUNTE S.V.": "Pedestrian robbery, non-violent",
  "ROBO A CUENTAHABIENTE C.V.": "Bank-customer robbery, violent",
  "ROBO A BORDO DE METRO C.V.": "Metro robbery, violent",
  "ROBO A BORDO DE METRO S.V.": "Metro robbery, non-violent",
  "ROBO A NEGOCIO C.V.": "Business robbery, violent",
  "ROBO DE VEHICULO AUTOMOTOR C.V.": "Car theft, violent",
  "ROBO DE VEHICULO AUTOMOTOR S.V.": "Car theft, non-violent",
  "ROBO A BORDO DE TAXI C.V.": "Taxi robbery, violent",
  "ROBO A BORDO DE MICROBUS C.V.": "Microbus robbery, violent",
  "ROBO A BORDO DE MICROBUS S.V.": "Microbus robbery, non-violent",
  "ROBO A TRANSPORTISTA C.V.": "Delivery-van robbery, violent",
};

/**
 * Share of a weekday's people-in-transit in each clock hour (0 to 23) across the Mexico City
 * metropolitan area, from INEGI's 2017 origin-destination survey. Presence, not departures: see the
 * provenance note in `crowd-curve.json`.
 */
export const CROWD_CURVE: readonly number[] = crowd.values;

/**
 * Reported pedestrian muggings (ROBO A TRANSEUNTE C.V.) in Mexico City by hour of day, index 0 to
 * 23. The endpoint and the window the counts cover are in `incident-curve.json`; they are baked in
 * so nothing is fetched at runtime, and kept in JSON so the Kotlin side reads the same figures.
 */
export const MUGGINGS_BY_HOUR: readonly number[] = incidents.values;

if (CROWD_CURVE.length !== 24 || MUGGINGS_BY_HOUR.length !== 24) {
  // A malformed curve would silently skew every level in the app, so fail at import instead.
  throw new Error("crowd-curve.json and incident-curve.json must each hold 24 hourly values");
}

/** The clamp on the multiplier: busy daytime streets may shave a little, the small hours double. */
export const HOUR_MULTIPLIER_MIN = 0.85;
export const HOUR_MULTIPLIER_MAX = 2.0;
/** Hours whose average defines "normal" and so anchors the multiplier at 1.0. */
const ANCHOR_FROM = 9;
const ANCHOR_TO = 19;

const sum = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);
const total = sum(MUGGINGS_BY_HOUR);

/**
 * Muggings per person out, square-rooted. Raw counts peak in the evening only because that is when
 * the streets are fullest, and would make 4am look safe; dividing by how many people are out gives
 * risk per person, which peaks in the small hours. The square root damps it, since a quiet street
 * raises a pedestrian's risk by less than the headcount falls.
 */
const riskPerPerson = (h: number) => Math.sqrt(MUGGINGS_BY_HOUR[h]! / total / CROWD_CURVE[h]!);

const anchor =
  sum(
    Array.from({ length: ANCHOR_TO - ANCHOR_FROM + 1 }, (_, i) => riskPerPerson(ANCHOR_FROM + i)),
  ) /
  (ANCHOR_TO - ANCHOR_FROM + 1);

/**
 * How much the hour scales a sector's danger, 1.0 being the daytime norm. On foot the full swing
 * applies; in a vehicle it is halved toward 1.0, because an empty street makes a pedestrian
 * conspicuous and a passenger not, so the full swing would double-count.
 */
export function hourMultiplier(hour: number, mode: Mode): number {
  if (!Number.isFinite(hour)) return 1; // an unknown hour moves nothing
  const h = ((Math.floor(hour) % 24) + 24) % 24;
  const onFoot = Math.min(
    HOUR_MULTIPLIER_MAX,
    Math.max(HOUR_MULTIPLIER_MIN, riskPerPerson(h) / anchor),
  );
  return mode === "FOOT" ? onFoot : (onFoot + 1) / 2;
}

/** The level once the hour and mode have been applied to the sector's base score. */
export function adjustedLevel(score: number, hour: number, mode: Mode): Level {
  return levelOf(score * hourMultiplier(hour, mode));
}

/** Below this speed the traveller is on foot. */
export const MODE_FOOT_MAX_MPS = 2;
/** Above this speed the traveller is in a vehicle. */
export const MODE_VEHICLE_MIN_MPS = 5;

/**
 * Between the two thresholds the previous mode holds, so a jog or a taxi crawling in traffic does
 * not swap the whole map back and forth. A speed that is not finite carries no information either,
 * whether NaN or an infinity, so it holds the previous mode too rather than swapping on a glitch.
 */
export function modeFromSpeed(speedMps: number, prev: Mode): Mode {
  if (!Number.isFinite(speedMps)) return prev;
  if (speedMps < MODE_FOOT_MAX_MPS) return "FOOT";
  if (speedMps > MODE_VEHICLE_MIN_MPS) return "VEHICLE";
  return prev;
}

/**
 * One police sector as the committed crime snapshot carries it. `geometry` is GeoJSON, so every
 * coordinate in it is `[lon, lat]`, the opposite of the `(lat, lon)` order the functions below take.
 */
export type SectorSnapshot = {
  sector: string;
  alcaldia: string;
  population: number;
  geometry: { type: "MultiPolygon"; coordinates: number[][][][] };
  /**
   * Reported count over the snapshot's window for every crime either weight map names, 0 when the
   * sector reported none. These are the scores' own inputs: each mode's score is the weighted sum
   * of these counts per thousand residents.
   */
  crimes: Record<string, number>;
  foot: { score: number; level: number };
  vehicle: { score: number; level: number };
};

/** Metres per degree of latitude; near enough everywhere for distances of tens of metres. */
const M_PER_DEG_LAT = 111_320;

/**
 * Even-odd crossing test of one ring, the standard half-open rule: an edge counts when it straddles
 * the point's latitude with one end strictly above it and the other not. Because the rule is
 * half-open, a point on an edge or a vertex is decided the same way for every ring that shares that
 * edge, so two neighbouring sectors never both claim it and never both miss it. Which neighbour gets
 * it is arbitrary, and at these scales it does not matter; that it is exactly one does.
 */
function inRing(lon: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi! > lat !== yj! > lat && lon < ((xj! - xi!) * (lat - yi!)) / (yj! - yi!) + xi!) {
      inside = !inside;
    }
  }
  return inside;
}

/** The first ring of a polygon is its outer boundary; every later ring is a hole inside it. */
function inPolygon(lon: number, lat: number, rings: number[][][]): boolean {
  if (rings.length === 0 || !inRing(lon, lat, rings[0]!)) return false;
  for (let k = 1; k < rings.length; k++) if (inRing(lon, lat, rings[k]!)) return false;
  return true;
}

/** Whether the point is inside the box around a polygon's outer ring; far cheaper than a ray cast. */
function inBox(lon: number, lat: number, outer: number[][]): boolean {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const [x, y] of outer) {
    if (x! < minX) minX = x!;
    if (x! > maxX) maxX = x!;
    if (y! < minY) minY = y!;
    if (y! > maxY) maxY = y!;
  }
  return lon >= minX && lon <= maxX && lat >= minY && lat <= maxY;
}

/**
 * The sector a coordinate is in, or `null` when it is in none. `null` is how this system says
 * "unknown": the caller shows "no data", and nothing here invents a score for it. A coordinate that
 * is not finite is in no sector. If sectors were ever to overlap, the first in the array wins.
 */
export function sectorAt(
  lat: number,
  lon: number,
  sectors: SectorSnapshot[],
): SectorSnapshot | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  for (const s of sectors) {
    for (const rings of s.geometry.coordinates) {
      const outer = rings[0];
      if (outer && inBox(lon, lat, outer) && inPolygon(lon, lat, rings)) return s;
    }
  }
  return null;
}

/**
 * The least distance in metres from a point to the boundary of a sector, holes included. The
 * degrees are flattened onto a plane around the point, which is exact enough over tens of metres.
 */
function metresToBoundary(lat: number, lon: number, s: SectorSnapshot): number {
  const kx = M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180);
  let best = Infinity;
  for (const rings of s.geometry.coordinates) {
    for (const ring of rings) {
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const ax = (ring[j]![0]! - lon) * kx;
        const ay = (ring[j]![1]! - lat) * M_PER_DEG_LAT;
        const bx = (ring[i]![0]! - lon) * kx;
        const by = (ring[i]![1]! - lat) * M_PER_DEG_LAT;
        const dx = bx - ax;
        const dy = by - ay;
        const len2 = dx * dx + dy * dy;
        const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, -(ax * dx + ay * dy) / len2));
        best = Math.min(best, Math.hypot(ax + t * dx, ay + t * dy));
      }
    }
  }
  return best;
}

/**
 * Even a perfect fix has to be this far into a new sector before we move. Urban GPS drifts by tens
 * of metres, and a sector border is a line, so without a margin a phone resting near one would
 * flap between two sectors, and Task 7 would turn each flap into a warning for a border never crossed.
 */
export const SECTOR_HYSTERESIS_M = 40;

/**
 * The most a coarse fix can hold the previous sector for. An indoor network fix can claim a
 * kilometre or more, and without a ceiling that would pin the previous sector across most of the
 * city, a confidently stale level for somebody who has walked into a worse one. Past the ceiling the
 * answer is whatever `sectorAt` says for the reported position, not `null`: a safety layer that goes
 * quiet reads as "fine", so a current best guess beats both a stale hold and a blank. 250 m is well
 * beyond any plausible GPS ambiguity at a border, so real border cases still hold, and far short of
 * a sector's own width.
 */
export const SECTOR_HOLD_MAX_M = 250;

/**
 * Like `sectorAt`, but steady near a border. While the fix is still within reach of the previous
 * sector's boundary, that sector holds; it is released only once the fix is clearly elsewhere. Reach
 * is the fix's own accuracy radius, never less than `SECTOR_HYSTERESIS_M` nor more than
 * `SECTOR_HOLD_MAX_M`, so a coarse fix holds longer and a sharp one still needs the margin. An accuracy that is not finite carries no
 * information, so only the margin applies. A `prev` that names no sector is treated as no previous
 * sector. When the fix is in no sector at all, `prev` still holds within reach and otherwise the
 * answer is `null`, the same unknown `sectorAt` gives.
 */
export function stableSectorAt(
  prev: string | null,
  lat: number,
  lon: number,
  accuracyM: number,
  sectors: SectorSnapshot[],
): SectorSnapshot | null {
  const here = sectorAt(lat, lon, sectors);
  if (prev === null || here?.sector === prev) return here;
  const held = sectors.find((s) => s.sector === prev);
  if (!held || !Number.isFinite(lat) || !Number.isFinite(lon)) return here;
  const reach = Math.min(
    Number.isFinite(accuracyM) ? Math.max(accuracyM, SECTOR_HYSTERESIS_M) : SECTOR_HYSTERESIS_M,
    SECTOR_HOLD_MAX_M,
  );
  return metresToBoundary(lat, lon, held) <= reach ? held : here;
}

/** The default rise that warrants interrupting somebody: one level. Configurable, never absolute. */
export const DEFAULT_RISE_THRESHOLD = 1;

/**
 * In a vehicle a rise only warns when the destination is at least this bad. At 50 km/h a border
 * arrives every couple of minutes, so the unsuppressed rule would fire dozens of times a journey;
 * driving into Tepito still warrants saying so, driving through Narvarte does not.
 */
export const VEHICLE_WARN_MIN_LEVEL: Level = 5;

/**
 * How long the caller should wait before repeating a warning for the same pair of sectors, so that
 * pacing a boundary or stopping on one does not ring over and over. The rule below is stateless and
 * does not apply this itself; the value follows the API's `TASK_PING_COOLDOWN_MS`, the house
 * precedent for not pestering people, rather than inventing a second number.
 */
export const ALERT_COOLDOWN_MS = 10 * 60_000;

export type AlertKind = "WARN" | "SAFER" | "NONE";

/**
 * Where the traveller is and where the projection says they are heading, by sector name. Either end
 * may be `null`: outside CDMX, or in a gap between polygons, there is no sector, and `sectorAt`
 * already says so that way. `sectors` is the committed snapshot, passed in rather than imported so
 * that this module stays as free of data as `sectorAt` is.
 */
export type ZoneAlertInput = {
  from: string | null;
  to: string | null;
  hour: number;
  mode: Mode;
  sectors: SectorSnapshot[];
  threshold?: number;
};

/** `from` and `to` are the two adjusted levels, `null` for an end whose sector is unknown. */
export type ZoneAlert = {
  kind: AlertKind;
  from: Level | null;
  to: Level | null;
  delta: number;
};

/**
 * The adjusted level of one named sector, or `null` when the name belongs to no sector in the
 * snapshot. The score is the one for the mode: a car and a pedestrian are on different maps, and
 * the snapshot's own `level` fields are the unadjusted ones, so they are not used here.
 */
function levelOfSector(
  name: string | null,
  hour: number,
  mode: Mode,
  sectors: SectorSnapshot[],
): Level | null {
  if (name === null) return null;
  const s = sectors.find((x) => x.sector === name);
  if (!s) return null;
  return adjustedLevel(mode === "FOOT" ? s.foot.score : s.vehicle.score, hour, mode);
}

/**
 * Whether walking or driving into the projected sector is worth saying anything about. The two
 * sectors are compared at the same hour and in the same mode, so an hour that lifts both equally is
 * no event at all — which is what keeps a phone standing still quiet at midnight, when the
 * multiplier reaches its ×2.0 ceiling and 68 of the 72 sectors reach level 4. A rise of the
 * threshold or more warns; any fall is reported silently; anything else is nothing.
 *
 * An end whose sector is unknown has no level, and no comparison is possible: the answer is `NONE`
 * with that end `null`, never level 1. Level 1 would turn stepping outside the covered area into an
 * improvement and stepping back in into a five-level warning, both invented. A threshold that is
 * not finite means "no information" and falls back to the default, because leaving it in a
 * comparison would silence the layer in TypeScript (`delta >= NaN` is always false) and flood it in
 * Kotlin (`NaN.toInt()` is 0). Whatever the threshold ends up being, default or given, it is then
 * lifted to at least 1: alerting on a rise of 0 is alerting on an absolute level, which the spec
 * rules out, and the floor has to sit outside that choice so that lowering the default one day
 * cannot reopen it.
 */
export function zoneAlert(input: ZoneAlertInput): ZoneAlert {
  const { from, to, hour, mode, sectors, threshold } = input;
  const a = levelOfSector(from, hour, mode, sectors);
  const b = levelOfSector(to, hour, mode, sectors);
  if (a === null || b === null) return { kind: "NONE", from: a, to: b, delta: 0 };

  const given = threshold ?? DEFAULT_RISE_THRESHOLD;
  const t = Math.max(1, Number.isFinite(given) ? given : DEFAULT_RISE_THRESHOLD);
  const delta = b - a;
  let kind: AlertKind = delta >= t ? "WARN" : delta <= -1 ? "SAFER" : "NONE";
  if (kind === "WARN" && mode === "VEHICLE" && b < VEHICLE_WARN_MIN_LEVEL) kind = "NONE";
  return { kind, from: a, to: b, delta };
}

/**
 * Where the traveller will be in `seconds` if they keep going the way they are going. `headingDeg`
 * is degrees clockwise from true north, as Android reports a bearing: 0 is north, 90 is east. The
 * degrees are flattened onto a plane, the same approximation `metresToBoundary` makes, which is
 * exact enough over the tens or hundreds of metres a lookahead covers.
 *
 * A heading, a speed or a lookahead that is not finite carries no information, and neither does a
 * speed or a lookahead at or below zero: there is nowhere to project to, so the origin comes back
 * unchanged. A phone standing still therefore compares its sector with itself, which is never an
 * alert. An origin that is not finite is handed back as it came rather than replaced — 0,0 is a
 * real place in the Gulf of Guinea, and `sectorAt` already answers `null` for a coordinate like
 * this.
 */
export function projectAhead(
  lat: number,
  lon: number,
  headingDeg: number,
  speedMps: number,
  seconds: number,
): { lat: number; lon: number } {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return { lat, lon };
  if (!Number.isFinite(headingDeg) || !Number.isFinite(speedMps) || !Number.isFinite(seconds))
    return { lat, lon };
  if (speedMps <= 0 || seconds <= 0) return { lat, lon };
  const metres = speedMps * seconds;
  const rad = (headingDeg * Math.PI) / 180;
  return {
    lat: lat + (metres * Math.cos(rad)) / M_PER_DEG_LAT,
    lon: lon + (metres * Math.sin(rad)) / (M_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180)),
  };
}
